import { WorkingHour } from 'interfaces/clinicSchedule';
import { FC } from 'react';
import { LunchBreakItem } from 'utils/normalizeEventBreak';
import SelectTime from '../../DayTimeSlot/LunchBreakSection/SelectTime/SelectTime';
import { ReactComponent as CloseIcon } from 'assets/icons/clear.svg';

interface LunchBreakSectionProps {
  isWorkingDay: boolean;
  lunchBreaks: LunchBreakItem;
  workingHours: WorkingHour;
  onChange: (updatedLunchBreaks: LunchBreakItem) => void;
}

const day = 'DAY';

const LunchBreakSection: FC<LunchBreakSectionProps> = ({
  isWorkingDay,
  lunchBreaks,
  workingHours,
  onChange,
}) => {
  const isLunchBreakSetup = lunchBreaks.startFirst && lunchBreaks.endFirst;

  const isSecondLunchBreakSetup =
    lunchBreaks.startSecond && lunchBreaks.endSecond;

  const onAddLunchBreak = () => {
    onChange({
      ...lunchBreaks,
      startFirst: workingHours.start,
      endFirst: workingHours.end,
    });
  };

  const onAddAnotherLunchBreak = () => {
    onChange({
      ...lunchBreaks,
      startSecond: lunchBreaks.endFirst,
      endSecond: workingHours.end,
    });
  };

  const onRemoveFirstLunchBreak = () => {
    const result = isSecondLunchBreakSetup
      ? {
          startFirst: lunchBreaks.startSecond,
          endFirst: lunchBreaks.endSecond,
          startSecond: null,
          endSecond: null,
        }
      : {
          startFirst: null,
          endFirst: null,
        };

    onChange({
      ...lunchBreaks,
      ...result,
    });
  };

  const onRemoveSecondLunchBreak = () => {
    onChange({
      ...lunchBreaks,
      startSecond: null,
      endSecond: null,
    });
  };

  const onChangeValue = (
    _: string,
    value: string,
    type: string,
    position: 'First' | 'Second' = 'First'
  ) => {
    const updatedLunchBreaks = {
      ...lunchBreaks,
      [`${type}${position}`]: value,
    };
    onChange(updatedLunchBreaks);
  };

  return (
    <>
      {!isLunchBreakSetup && (
        <button
          className="text-magenta text-14 leading-[2.1rem] font-bold pl-13.4 disabled:opacity-50"
          disabled={!isWorkingDay}
          onClick={onAddLunchBreak}
        >
          + Add lunch break
        </button>
      )}
      {isLunchBreakSetup && (
        <>
          <div className="flex items-center mt-2.4">
            <span className="text-13 leading-[2rem] whitespace-nowrap">
              Lunch Break(s)
            </span>
            <div className="flex flex-1 ml-4.4 items-center justify-between relative">
              <SelectTime
                day={day}
                start={workingHours.start!}
                end={lunchBreaks.endFirst!}
                type={'start'}
                value={lunchBreaks.startFirst!}
                onChange={(...args) => onChangeValue(...args)}
                isInEditModal
              />
              <div className="h-full mx-1">-</div>
              <SelectTime
                day={day}
                start={lunchBreaks.startFirst!}
                end={workingHours.end!}
                type={'end'}
                value={lunchBreaks.endFirst!}
                onChange={(...args) => onChangeValue(...args)}
                isInEditModal
              />
              <button
                className="absolute -right-3"
                onClick={onRemoveFirstLunchBreak}
              >
                <CloseIcon className="w-1.6 h-1.6 fill-darkest-grey" />
              </button>
            </div>
          </div>
          {isSecondLunchBreakSetup ? (
            <div className="flex items-center mt-2.4">
              <div className="flex flex-1 ml-13.4 items-center justify-between relative">
                <SelectTime
                  day={day}
                  type={'start'}
                  start={lunchBreaks.endFirst!}
                  end={lunchBreaks.endSecond!}
                  value={lunchBreaks.startSecond!}
                  onChange={(...args) => onChangeValue(...args, 'Second')}
                  isInEditModal
                />
                <div className="h-full mx-1">-</div>
                <SelectTime
                  day={day}
                  type={'end'}
                  start={lunchBreaks.startSecond!}
                  end={workingHours.end!}
                  value={lunchBreaks.endSecond!}
                  onChange={(...args) => onChangeValue(...args, 'Second')}
                  isInEditModal
                />
                <button
                  className="absolute -right-3"
                  onClick={onRemoveSecondLunchBreak}
                >
                  <CloseIcon className="w-1.6 h-1.6 fill-darkest-grey" />
                </button>
              </div>
            </div>
          ) : (
            <button
              className="text-magenta text-14 leading-[2.1rem] font-bold pl-13.4 disabled:opacity-50 mt-1.3"
              disabled={!isWorkingDay}
              onClick={onAddAnotherLunchBreak}
            >
              + Add another
            </button>
          )}
        </>
      )}
    </>
  );
};

export default LunchBreakSection;
